import { Intent } from "lib/common/enums";
import useFormDataInput from "lib/hooks/useFormDataInput";
import React, { useEffect, useRef, useState } from "react";
import Button from "../atomic/Button";
import { ButtonSpinner } from "../atomic/Spinner/Spinner";
import TextArea from "../atomic/TextArea";
import TextField from "../atomic/TextField";
import FileDrop from "../FileDrop";
import { Form } from "../Form";
import FormControl from "../Form/FormControl";
import Spacer from "../Form/Spacer";
import { BugIcon, CloseIcon } from "../Icon";

interface BugReportProps {
  onHide?: () => void;
}

function BugReport({ onHide }: BugReportProps) {
  const [files, setFiles] = useState<File[]>([]);
  const { reqWithFormData, loading, success } = useFormDataInput();

  const resetButton = useRef<HTMLButtonElement>(null);

  const addFile = (fileData: File[]) => {
    setFiles((prev) => [...prev, ...fileData]);
  };

  const handleSubmit = (val: any) => {
    const formData = new FormData();
    formData.append("title", val.title);
    formData.append("reporterName", val.fullname);
    formData.append("reporterEmail", val.email);
    formData.append("description", val.description);
    files.map((file) => {
      formData.append("images", file);
    });
    formData.append("url", val.url);

    reqWithFormData("post", "/bug_report/", formData);
    setFiles([]);

    onHide && onHide();
  };

  useEffect(() => {
    if (success) {
      resetButton.current?.click();
    }
  }, [success, onHide]);

  return (
    <div className="bg-white p-5 my-6 mx-auto max-h-screen container rounded ">
      <div className="flex flex-wrap items-center relative">
        <BugIcon />
        <div className="ml-4 normal-case flex-1">
          <h5 className="text-gray-800">Report a Bug</h5>
          <div className="hidden sm:block">
            <p className="text-gray mb-0">
              We would love the help from our community to
            </p>
            <p className="text-gray"> help improve the platform.</p>
          </div>
          <div className="sm:hidden">
            <p className="text-gray mb-0">
              We would love the help from our community to help improve the
              platform.
            </p>
          </div>
        </div>
        {onHide && (
          <p
            onClick={() => onHide()}
            className=" hover:text-error-900 text-error rounded-full absolute top-2 right-2 cursor-pointer  transition-all"
          >
            <CloseIcon />
          </p>
        )}
      </div>
      <Form onSubmit={handleSubmit} className="mt-3">
        <Spacer className="flex ">
          <Spacer className="flex-1 mr-2">
            <FormControl label="Full Name" name="fullname" required>
              <TextField className="bg-gray-50 rounded" />
            </FormControl>
          </Spacer>
          <Spacer className="flex-1">
            <FormControl
              validationRules={[
                {
                  rule: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                  message: "Please enter a valid email address",
                  intent: Intent.ERROR,
                },
              ]}
              label="Email"
              name="email"
              required
            >
              <TextField type="email" className="bg-gray-50 rounded " />
            </FormControl>
          </Spacer>
        </Spacer>

        <FormControl required className="mt-1" label="Title" name="title">
          <TextField className="bg-gray-50 rounded" />
        </FormControl>
        <FormControl
          required
          className="my-2"
          label="Description"
          name="description"
        >
          <TextArea
            className="bg-gray-50 rounded"
            placeholder="Please describe the bug"
            row={3}
          />
        </FormControl>
        <FormControl
          className="mt-1"
          validationRules={[
            {
              rule: /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/,
              message: "Please enter a valid URL.",
              intent: Intent.ERROR,
            },
          ]}
          label="URL"
          name="url"
        >
          <TextField className="bg-gray-50 rounded" />
        </FormControl>
        <FormControl required className="mt-2" label="Screenshots" name="files">
          <FileDrop files={files} addFile={addFile} />
        </FormControl>
        <Button type="submit" className="mt-5" block disabled={loading}>
          {loading ? (
            <div>
              <span className="mr-3">Reporting...</span>
              <ButtonSpinner />
            </div>
          ) : (
            <span>Submit</span>
          )}
        </Button>
        <Button ref={resetButton} className="hidden" type="reset">
          reset
        </Button>
      </Form>
    </div>
  );
}

export default BugReport;
